import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MarkerClusterer as GoogleMapsMarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { APIProvider, AdvancedMarker, InfoWindow, Map, useApiIsLoaded, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import assetsCluster from "../../assets/AssetsCluster.svg";
import { useIsRefreshedDashboardFlag } from "../../hooks/useAppConfigFlags";
import { getBatteryIcon } from "../../lib/getBatteryIcon";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
import { getCESTLocalTime, getNodeIconColor } from "../../pages/Projects/ProjectDetails.helper";
import { formatTimestampWithTimezone } from "../../shared/timestampHelpers";
import { REFRESHED_NEUTRAL_TEXT_CLASS, getEffectiveStatus, getRefreshedStatusTextClassName } from "../../utils/getRefreshedStatusTextColor";
import { assetLocationToLatLng, getSiteViewport } from "./MapViewport.helper";
import "./ProjectDetailsMaps.css";
const MAP_LOAD_DELAY_MS = 500;
const HOVER_TOOLTIP_DELAY_MS = 150;
const DEFAULT_MAP_ZOOM = 8;
const MAP_ID_DEMO = "DEMO_MAP_ID";
const WAYPOINT_HUB = "WattHub";
const ROUTE_COLOR_PRIMARY = "#0366ff";
const ROUTE_COLOR_SECONDARY = "#FB5A00";
const ROUTE_STROKE_WEIGHT = 6;
const ROUTE_STROKE_OPACITY_SOLID = 1;
const ROUTE_STROKE_OPACITY_FAINT = 0.2;
const ROUTE_DASH_REPEAT = "20px";
const ROUTE_PRIMARY_Z_INDEX = 1;
const MIN_POLYLINE_POINTS = 2;
const POLYLINE_DEFAULT_STROKE_WEIGHT = 2;
const POLYLINE_MIN_TOUCH_WEIGHT = 8;
const CLUSTER_ICON_SIZE = "53px";
const INFO_WINDOW_PIXEL_OFFSET_Y_OPS = -100;
const INFO_WINDOW_PIXEL_OFFSET_Y_LIVE = -50;
const GEO_BOUNDS_WORLD_DIM = { height: 256, width: 256 };
const GEO_BOUNDS_ZOOM_MAX = 21;
const GEO_BOUNDS_DEGREES_HALF = 180;
const GEO_BOUNDS_DEGREES_FULL = 360;
// Marker cards are ~100px tall and hang above their coordinate, so the top needs more room.
const SITE_FIT_PADDING = { bottom: 60, left: 60, right: 60, top: 110 };
// Without a ceiling, fitBounds drops to street level whenever assets sit close together.
const SITE_FIT_MAX_ZOOM = 17;
// Two stacked pins are exactly the case the count badge exists for.
const CLUSTER_MIN_ASSET_COUNT = 2;
// Roughly one marker footprint: pins further apart than this are already readable on their own.
const CLUSTER_RADIUS_PX = 45;
// Last zoom that still groups, so the first zoom-in past the site-fit ceiling splits a cluster.
const CLUSTER_MAX_ZOOM = SITE_FIT_MAX_ZOOM;
const DENS_MAKE = "DENS";
const API_KEY = getGoogleMapsApiKey();
/**
 * Format asset name for display on map
 * - For DENS assets: always show exactly 4 characters (first 2 + last 2)
 * - For other assets: show full name
 */
const formatAssetNameForMap = (assetName, assetMetadata) => {
    if (assetMetadata?.make === DENS_MAKE && assetName && assetName.length >= 4) {
        const truncated = assetName.substring(0, 2) + assetName.substring(assetName.length - 2);
        return truncated;
    }
    if (assetMetadata?.make === DENS_MAKE && assetName && assetName.length < 4) {
        return assetName;
    }
    return assetName;
};
// SOC arrives as a numeric string from the project-details response; NaN marks it unavailable.
const parseSoc = (soc) => soc === null || soc === undefined || String(soc).trim() === "" ? Number.NaN : Number(soc);
const MapInstanceBinder = ({ setMap }) => {
    const map = useMap();
    useEffect(() => {
        if (map) {
            setMap(map);
        }
    }, [map, setMap]);
    return null;
};
const MapPolyline = ({ onMouseOut, onMouseOver, options, path }) => {
    const map = useMap();
    const polyline = useMemo(() => new google.maps.Polyline(), []);
    const callbacks = useRef({ onMouseOut, onMouseOver });
    useEffect(() => {
        callbacks.current = { onMouseOut, onMouseOver };
    }, [onMouseOver, onMouseOut]);
    useEffect(() => {
        if (!path || path.length < MIN_POLYLINE_POINTS) {
            polyline.setMap(null);
            return;
        }
        polyline.setOptions({
            ...options,
            clickable: true,
            path,
            strokeWeight: Math.max(options.strokeWeight || POLYLINE_DEFAULT_STROKE_WEIGHT, POLYLINE_MIN_TOUCH_WEIGHT)
        });
    }, [path, options, polyline]);
    useEffect(() => {
        if (!map || !path || path.length < MIN_POLYLINE_POINTS)
            return;
        polyline.setMap(map);
        const gMapsEvent = google.maps.event;
        gMapsEvent.addListener(polyline, "mouseover", (event) => {
            callbacks.current.onMouseOver?.(event);
        });
        gMapsEvent.addListener(polyline, "mouseout", () => {
            callbacks.current.onMouseOut?.();
        });
        return () => {
            gMapsEvent.clearInstanceListeners(polyline);
            polyline.setMap(null);
        };
    }, [map, path, polyline]);
    return null;
};
const LiveDashBoardOpsPlannerMapContent = ({ addressDetails, addressDirections = [], isRefreshedLayout = false, mapCenter, selectedAddress, siteCenter, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
siteOffset: _siteOffset, siteTimezone }) => {
    const [map, setMap] = useState(null);
    const [mapType, setMapType] = useState("");
    const [infoWindowOpen, setInfoWindowOpen] = useState(null);
    const [hoveredMarker, setHoveredMarker] = useState(null);
    const [mapDelay, setMapDelay] = useState(false);
    const isLoaded = useApiIsLoaded();
    const markerLibrary = useMapsLibrary("marker");
    const isOpsPlannerMap = !!addressDirections?.length;
    const shouldClusterMarkers = !isOpsPlannerMap && addressDetails.length >= CLUSTER_MIN_ASSET_COUNT;
    const isFlagEnabled = useIsRefreshedDashboardFlag();
    // Opt-in only: shared consumers outside the Live Dashboard keep the existing layout.
    const isRefreshedDashboard = isRefreshedLayout && isFlagEnabled;
    const [tooltip, setTooltip] = useState(null);
    const clustererRef = useRef(null);
    const [markers, setMarkers] = useState({});
    const fittedSignatureRef = useRef(null);
    const clampZoomListenerRef = useRef(null);
    const hadSelectionRef = useRef(false);
    // The project pin anchors the view on the worksite even when every asset is parked together.
    const sitePoints = useMemo(() => {
        const assetPoints = addressDetails
            .map((details) => assetLocationToLatLng(details.location))
            .filter((point) => point !== null);
        return siteCenter ? [siteCenter, ...assetPoints] : assetPoints;
    }, [addressDetails, siteCenter]);
    // Re-frame when an asset joins or leaves, not on every position poll, so a
    // refresh never yanks the camera away from wherever the user panned to.
    const fitSignature = useMemo(() => [
        siteCenter ? `${siteCenter.lat},${siteCenter.lng}` : "no-site-center",
        ...addressDetails
            .map((details, index) => details.assetId ?? `index-${index}`)
            .sort((left, right) => left.localeCompare(right))
    ].join("|"), [addressDetails, siteCenter]);
    useEffect(() => {
        if (!map)
            return;
        const mapTypeListener = map.addListener("maptypeid_changed", () => {
            const currentType = map.getMapTypeId();
            setMapType(currentType || "");
        });
        return () => {
            mapTypeListener.remove();
        };
    }, [map]);
    const fitToSite = useCallback(() => {
        if (!map)
            return false;
        const viewport = getSiteViewport(sitePoints);
        if (!viewport)
            return false;
        map.fitBounds(viewport, SITE_FIT_PADDING);
        clampZoomListenerRef.current?.remove();
        clampZoomListenerRef.current = google.maps.event.addListenerOnce(map, "idle", () => {
            clampZoomListenerRef.current = null;
            const zoom = map.getZoom();
            if (zoom !== undefined && zoom > SITE_FIT_MAX_ZOOM) {
                map.setZoom(SITE_FIT_MAX_ZOOM);
            }
        });
        return true;
    }, [map, sitePoints]);
    useEffect(() => () => {
        clampZoomListenerRef.current?.remove();
        clampZoomListenerRef.current = null;
    }, []);
    useEffect(() => {
        if (!map || fittedSignatureRef.current === fitSignature)
            return;
        if (fitToSite())
            fittedSignatureRef.current = fitSignature;
    }, [map, fitSignature, fitToSite]);
    useEffect(() => {
        const timer = setTimeout(() => {
            setMapDelay(true);
        }, MAP_LOAD_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);
    const getBoundsZoomLevel = (bounds, mapDim) => {
        function latRad(lat) {
            const sin = Math.sin((lat * Math.PI) / GEO_BOUNDS_DEGREES_HALF);
            const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
            return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
        }
        function zoom(mapPx, worldPx, fraction) {
            return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
        }
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
        const lngDiff = ne.lng() - sw.lng();
        const lngFraction = (lngDiff < 0 ? lngDiff + GEO_BOUNDS_DEGREES_FULL : lngDiff) / GEO_BOUNDS_DEGREES_FULL;
        const latZoom = zoom(mapDim.height, GEO_BOUNDS_WORLD_DIM.height, latFraction);
        const lngZoom = zoom(mapDim.width, GEO_BOUNDS_WORLD_DIM.width, lngFraction);
        return Math.min(latZoom, lngZoom, GEO_BOUNDS_ZOOM_MAX);
    };
    const buildClusterContent = useCallback((count) => {
        const content = document.createElement("div");
        content.style.backgroundImage = `url(${assetsCluster})`;
        content.style.backgroundSize = "cover";
        content.style.width = CLUSTER_ICON_SIZE;
        content.style.height = CLUSTER_ICON_SIZE;
        content.style.display = "flex";
        content.style.alignItems = "center";
        content.style.justifyContent = "center";
        content.style.color = "white";
        content.style.fontWeight = "bold";
        content.innerText = count.toString();
        return content;
    }, []);
    useEffect(() => {
        if (!isLoaded || !map || !markerLibrary)
            return;
        if (!clustererRef.current) {
            clustererRef.current = new GoogleMapsMarkerClusterer({
                algorithm: new SuperClusterAlgorithm({
                    maxZoom: CLUSTER_MAX_ZOOM,
                    minPoints: CLUSTER_MIN_ASSET_COUNT,
                    radius: CLUSTER_RADIUS_PX
                }),
                map,
                renderer: {
                    render: ({ count, position }) => {
                        return new google.maps.marker.AdvancedMarkerElement({
                            content: buildClusterContent(count),
                            position
                        });
                    }
                }
            });
        }
    }, [isLoaded, map, markerLibrary, buildClusterContent]);
    useEffect(() => {
        if (!clustererRef.current)
            return;
        clustererRef.current.clearMarkers();
        if (shouldClusterMarkers) {
            clustererRef.current.addMarkers(Object.values(markers));
            return;
        }
        // Unmounted live markers stay cached in `markers`, so only the live map may
        // re-attach them — the Ops Planner renders its own and would show both sets.
        if (isOpsPlannerMap)
            return;
        // clearMarkers() leaves anything the clusterer had grouped detached from the map.
        Object.values(markers).forEach((marker) => {
            marker.map = map;
        });
    }, [markers, shouldClusterMarkers, isOpsPlannerMap, map]);
    useEffect(() => {
        const validKeys = new Set(addressDetails.map((details, index) => details.assetId ? details.assetId : `${isOpsPlannerMap ? "ops" : "default"}-${index}`));
        setMarkers((prev) => {
            const next = {};
            Object.entries(prev).forEach(([key, marker]) => {
                if (validKeys.has(key)) {
                    next[key] = marker;
                }
            });
            if (Object.keys(next).length === Object.keys(prev).length)
                return prev;
            return next;
        });
    }, [addressDetails, isOpsPlannerMap]);
    const setMarkerRef = useCallback((marker, key) => {
        setMarkers((prev) => {
            if (!marker)
                return prev;
            if (prev[key] === marker)
                return prev;
            return { ...prev, [key]: marker };
        });
    }, []);
    useEffect(() => {
        let zoomChangeListener;
        if (map) {
            const handleZoomChange = () => {
                setInfoWindowOpen(null);
                setHoveredMarker(null);
            };
            zoomChangeListener = map.addListener("zoom_changed", handleZoomChange);
            return () => {
                zoomChangeListener.remove();
            };
        }
    }, [map]);
    useEffect(() => {
        if (!map || !selectedAddress?.addressList)
            return;
        const { addressList } = selectedAddress;
        if (addressList.length > 0) {
            hadSelectionRef.current = true;
            const bounds = new google.maps.LatLngBounds();
            addressDetails.forEach((details) => {
                if (addressList.indexOf(details.assetId) >= 0) {
                    bounds.extend({
                        lat: Number(details.location[0]),
                        lng: Number(details.location[1])
                    });
                }
            });
            const mapElement = document.getElementById("live-dash-board-ops-planner-gmap");
            const mapDim = {
                height: mapElement?.offsetHeight,
                width: mapElement?.offsetWidth
            };
            const zoom = addressList.length === 1 ? getBoundsZoomLevel(bounds, mapDim) : getBoundsZoomLevel(bounds, mapDim) - 1;
            if (zoom) {
                map.setZoom(zoom);
                map.setCenter(bounds.getCenter());
            }
            return;
        }
        // An empty list also arrives when the project changes, so only a real deselect
        // re-frames — otherwise every poll would drag the camera back off the site fit.
        if (!hadSelectionRef.current)
            return;
        hadSelectionRef.current = false;
        if (fitToSite())
            fittedSignatureRef.current = fitSignature;
    }, [selectedAddress, map, addressDetails, fitSignature, fitToSite]);
    useEffect(() => {
        let timer;
        if (hoveredMarker) {
            timer = setTimeout(() => {
                setInfoWindowOpen(hoveredMarker);
            }, HOVER_TOOLTIP_DELAY_MS);
        }
        else {
            timer = setTimeout(() => {
                setInfoWindowOpen(null);
            }, HOVER_TOOLTIP_DELAY_MS);
        }
        return () => clearTimeout(timer);
    }, [hoveredMarker]);
    const onMapTypeIdChangeHandler = () => {
        if (!map)
            return;
        setMapType(map.getMapTypeId() || "");
    };
    const getDirectionRouteColor = (routeIndex) => {
        let colorName = "", zIndex = 0, strokeWeight = ROUTE_STROKE_WEIGHT;
        switch (routeIndex) {
            case 0:
                colorName = ROUTE_COLOR_PRIMARY;
                zIndex = ROUTE_PRIMARY_Z_INDEX;
                strokeWeight = ROUTE_STROKE_WEIGHT;
                break;
            case 1:
                colorName = ROUTE_COLOR_SECONDARY;
                break;
            case 2:
                colorName = ROUTE_COLOR_SECONDARY;
                break;
            default:
                colorName = ROUTE_COLOR_SECONDARY;
        }
        if (routeIndex === 1 || routeIndex > 2) {
            return {
                geodesic: true,
                icons: [
                    {
                        icon: {
                            path: "M 0,-2 0,2",
                            scale: 2,
                            strokeOpacity: ROUTE_STROKE_OPACITY_SOLID,
                            strokeWeight: ROUTE_STROKE_WEIGHT
                        },
                        offset: "100%",
                        repeat: ROUTE_DASH_REPEAT
                    }
                ],
                strokeColor: ROUTE_COLOR_SECONDARY,
                strokeOpacity: ROUTE_STROKE_OPACITY_FAINT,
                strokeWeight: ROUTE_STROKE_WEIGHT
            };
        }
        else {
            return {
                strokeColor: colorName,
                strokeOpacity: ROUTE_STROKE_OPACITY_SOLID,
                strokeWeight,
                zIndex
            };
        }
    };
    const getFormattedTooltip = (tooltipContent) => {
        const { addressDetail = null, addressDirection = null } = tooltipContent || {};
        if (addressDetail) {
            let tooltipList = addressDirections.filter((obj) => addressDetail.assetName === obj.srcAssetName || addressDetail.assetName === obj.destAssetName);
            tooltipList =
                tooltipList.length === 0
                    ? addressDirections.filter((obj) => obj.destAssetName === addressDetail.assetName)
                    : tooltipList;
            const updatedTooltipList = tooltipList.map((item) => {
                const { destAssetName = "", srcAssetName = "", srcAssetNameFull = "", srcAssetNameToOthers = false } = item;
                if (!srcAssetNameToOthers &&
                    addressDetail.assetName !== srcAssetNameFull &&
                    destAssetName === WAYPOINT_HUB) {
                    return {
                        ...item,
                        destAssetName: srcAssetName,
                        srcAssetName: srcAssetNameFull
                    };
                }
                else {
                    return item;
                }
            });
            return (_jsx("div", { "data-test": "info-window-asset-name-label", children: updatedTooltipList.map((item, index) => {
                    const { destAssetName = "", srcAssetName = "" } = item;
                    // Use nullish coalescing to handle 0 values
                    const distance = item.distance ?? "";
                    const duration = item.duration ?? "";
                    return (_jsxs("div", { className: "mb-2", children: [_jsx("div", { children: `From ${srcAssetName} to ${destAssetName}` }), _jsx("span", { children: `${distance !== "" ? distance : 0} km ` }), _jsxs("span", { children: [`/ ${duration !== "" ? duration : 0}`, typeof duration === "number" || !String(duration).toLowerCase().includes("minute")
                                        ? " minutes"
                                        : ""] })] }, `info-window-${index}`));
                }) }));
        }
        else if (addressDirection) {
            const { distance = "", duration = "", srcAssetNameFull = "", srcAssetNameToOthers = false } = addressDirection;
            let { destAssetName = "", srcAssetName = "" } = addressDirection;
            if (!srcAssetNameToOthers && destAssetName === WAYPOINT_HUB) {
                destAssetName = srcAssetName;
                srcAssetName = srcAssetNameFull;
            }
            return (_jsxs("div", { "data-test": "info-window-asset-name-label", children: [_jsx("div", { children: `From ${srcAssetName} to ${destAssetName}` }), _jsx("span", { children: `${distance !== "" ? distance : 0} km ` }), _jsxs("span", { children: [`/ ${duration !== "" ? duration : 0}`, typeof duration === "number" || (typeof duration === "string" && !/min/i.test(duration))
                                ? " minutes"
                                : ""] })] }));
        }
    };
    if (!isLoaded)
        return _jsx("div", { "data-test": "loading_map_state", children: "Loading..." });
    const getOpsPlannerMapContent = () => {
        return (_jsxs(_Fragment, { children: [addressDirections.map((route, index) => {
                    const polylinePath = (route.path || [])
                        .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng) }))
                        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
                    if (polylinePath.length < MIN_POLYLINE_POINTS) {
                        return null;
                    }
                    return (_jsx(MapPolyline, { onMouseOut: () => setTooltip(null), onMouseOver: (e) => {
                            if (!e.latLng)
                                return;
                            setTooltip({
                                lat: e.latLng.lat(),
                                lng: e.latLng.lng(),
                                text: route
                            });
                        }, options: getDirectionRouteColor(index), path: polylinePath }, `route-${index}`));
                }), _jsx("div", { children: addressDetails.map((details, index) => (_jsx(AdvancedMarker, { position: {
                            lat: Number(details.location[0]),
                            lng: Number(details.location[1])
                        }, children: _jsxs("div", { className: "flex flex-col items-center cursor-pointer pointer-events-auto", "data-test": `ops-marker-${details.assetId || index}`, onMouseEnter: () => {
                                setHoveredMarker(details);
                            }, onMouseLeave: () => {
                                setHoveredMarker(null);
                            }, children: [_jsx("div", { className: "bg-transparent text-xs font-semibold", children: formatAssetNameForMap(details.assetName, details.assetMetadata) }), _jsx("img", { alt: details.assetName || "asset-marker", className: "h-[76px] w-[100px] object-contain", "data-test": `ops-marker-icon-${details.assetId || index}`, src: details.iconUrl || "" })] }) }, `ops-marker-${details.assetId || index}`))) }), tooltip && (_jsx(InfoWindow, { onCloseClick: () => setTooltip(null), position: { lat: tooltip.lat, lng: tooltip.lng }, children: _jsx("div", { className: "pt-4", "data-test": "ops-route-tooltip", children: getFormattedTooltip({ addressDirection: tooltip.text }) }) })), infoWindowOpen && (_jsx(InfoWindow, { onCloseClick: () => setInfoWindowOpen(null), pixelOffset: [0, INFO_WINDOW_PIXEL_OFFSET_Y_OPS], position: {
                        lat: infoWindowOpen.location[0],
                        lng: infoWindowOpen.location[1]
                    }, children: _jsx("div", { className: "pt-4", "data-test": "ops-marker-info-window", children: getFormattedTooltip({ addressDetail: infoWindowOpen }) }) }))] }));
    };
    const getLiveDashBoardMapContent = () => {
        return (_jsxs(_Fragment, { children: [_jsx("div", { children: addressDetails.map((details, index) => {
                        const markerKey = details.assetId || `default-${index}`;
                        return (_jsx(AdvancedMarker, { position: {
                                lat: Number(details.location[0]),
                                lng: Number(details.location[1])
                            }, ref: (marker) => setMarkerRef(marker, markerKey), children: _jsxs("div", { className: "flex flex-col items-center cursor-pointer pointer-events-auto relative drop-shadow-md", "data-test": `live-marker-${markerKey}`, onMouseEnter: () => {
                                    setHoveredMarker(details);
                                }, onMouseLeave: () => {
                                    setHoveredMarker(null);
                                }, children: [_jsx("div", { className: "custom-marker-elem flex items-center justify-center gap-1 bg-white rounded-lg p-1.5", "data-test": `live-marker-label-${markerKey}`, children: isRefreshedDashboard ? (_jsxs("div", { className: "flex flex-col items-center gap-0.5 px-1", children: [_jsx("div", { className: "text-xs font-semibold text-[#1f1f1f]", children: formatAssetNameForMap(details.assetName, details.assetMetadata) }), _jsx("img", { alt: details.assetName || "asset-marker", className: "size-[50px] object-contain", "data-test": `live-marker-icon-${markerKey}`, src: details.iconUrl || "" }), (details.activity || details.assetStatus) && (_jsx("div", { className: `text-[8px] font-semibold ${getRefreshedStatusTextClassName(getEffectiveStatus(details.activity, details.assetStatus))}`, "data-test": `live-marker-status-${markerKey}`, children: getEffectiveStatus(details.activity, details.assetStatus) }))] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: `rounded-lg h-2 w-2 ${getNodeIconColor(details?.activity)}` }), _jsx("img", { alt: details.assetName || "asset-marker", className: "h-6 w-6 object-contain", "data-test": `live-marker-icon-${markerKey}`, src: details.iconUrl || "" }), _jsx("div", { className: "text-xs font-semibold h-4 leading-4 w-auto text-black", children: formatAssetNameForMap(details.assetName, details.assetMetadata) })] })) }), _jsx("div", { className: "absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 border-l-[8px] border-r-[8px] border-t-[9px] border-l-transparent border-r-transparent border-t-white h-0 w-0" })] }) }, markerKey));
                    }) }), infoWindowOpen && (_jsx(InfoWindow, { onCloseClick: () => setInfoWindowOpen(null), pixelOffset: [0, INFO_WINDOW_PIXEL_OFFSET_Y_LIVE], position: {
                        lat: infoWindowOpen.location[0],
                        lng: infoWindowOpen.location[1]
                    }, children: isRefreshedDashboard ? (_jsxs("div", { className: "flex flex-col gap-2 p-2 w-[220px]", "data-test": "live-marker-info-window", children: [infoWindowOpen.assetName && (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: `size-2 rounded-full ${getNodeIconColor(infoWindowOpen.activity, infoWindowOpen.assetStatus)}` }), _jsx("span", { className: "font-semibold text-sm text-[#1f1f1f]", "data-test": "info-window-asset-name", children: formatAssetNameForMap(infoWindowOpen.assetName, infoWindowOpen.assetMetadata) })] })), Number.isFinite(parseSoc(infoWindowOpen.soc)) && (_jsxs("div", { className: "flex items-center gap-1 text-xs", children: [_jsxs("span", { className: "font-semibold text-[#3f3f3f]", "data-test": "soc-value", children: [Math.round(parseSoc(infoWindowOpen.soc)), "%"] }), getBatteryIcon(parseSoc(infoWindowOpen.soc))] })), infoWindowOpen?.timestamp &&
                                (() => {
                                    const { formattedTime, timezoneAbbr } = formatTimestampWithTimezone(infoWindowOpen.timestamp, siteTimezone, "iso-date", (ts) => getCESTLocalTime(ts, "yyyy-MM-dd HH:mm"));
                                    return (_jsxs("span", { className: `text-xs ${REFRESHED_NEUTRAL_TEXT_CLASS}`, children: ["Last reported:", " ", _jsxs("span", { "data-test": "last-reported-time-zone-value", children: [formattedTime, " ", timezoneAbbr] })] }));
                                })()] })) : (_jsxs("div", { className: "text-sm pt-4", "data-test": "live-marker-info-window", children: [infoWindowOpen.assetName && (_jsxs("div", { children: [_jsxs("span", { className: "font-bold text-base", "data-test": "info-window-asset-name-label", children: ["Name :", " "] }), _jsx("span", { "data-test": "info-window-asset-name", children: formatAssetNameForMap(infoWindowOpen.assetName, infoWindowOpen.assetMetadata) })] })), infoWindowOpen.soc && (_jsxs("div", { className: "flex items-center", children: [_jsx("span", { className: "font-bold", "data-test": "soc", children: "SOC :" }), _jsxs("span", { className: "ml-1", "data-test": "soc-value", children: [Math.round(Number(infoWindowOpen.soc)), "%\u00A0"] }), getBatteryIcon(Number(infoWindowOpen?.soc))] })), infoWindowOpen?.timestamp && (_jsxs("div", { children: [_jsxs("span", { className: "font-bold", "data-test": "last-reported-time", children: ["Last Reported Time :", " "] }), infoWindowOpen.timestamp &&
                                        (() => {
                                            const { formattedTime, timezoneAbbr } = formatTimestampWithTimezone(infoWindowOpen.timestamp, siteTimezone, "iso-date", (ts) => getCESTLocalTime(ts, "yyyy-MM-dd HH:mm"));
                                            return (_jsxs("span", { "data-test": "last-reported-time-zone-value", children: [formattedTime, " ", timezoneAbbr] }));
                                        })()] }))] })) }))] }));
    };
    return (_jsx("div", { className: isRefreshedDashboard ? "h-full" : undefined, "data-test": "custom-google-map", children: mapDelay && (_jsxs(Map
        // mapId opts the map into Google's newer combined "camera control" pan-arrow
        // widget; the refreshed design wants only zoom +/-, so turn it off there.
        , { 
            // mapId opts the map into Google's newer combined "camera control" pan-arrow
            // widget; the refreshed design wants only zoom +/-, so turn it off there.
            cameraControl: isRefreshedDashboard ? false : undefined, className: isRefreshedDashboard ? "refreshed-map-controls h-full w-full" : "h-[562px] w-full", "data-test": "live-dashboard-ops-planner-map", defaultCenter: mapCenter, defaultZoom: DEFAULT_MAP_ZOOM, fullscreenControl: true, fullscreenControlOptions: isRefreshedDashboard ? { position: google.maps.ControlPosition.TOP_RIGHT } : undefined, gestureHandling: "greedy", id: "live-dash-board-ops-planner-gmap", mapId: MAP_ID_DEMO, mapTypeControl: true, mapTypeControlOptions: {
                mapTypeIds: [
                    google.maps.MapTypeId.ROADMAP,
                    google.maps.MapTypeId.SATELLITE,
                    google.maps.MapTypeId.HYBRID,
                    google.maps.MapTypeId.TERRAIN
                ],
                position: google.maps.ControlPosition.TOP_LEFT,
                style: google.maps.MapTypeControlStyle.DEFAULT
            }, mapTypeId: mapType || google.maps.MapTypeId?.ROADMAP, onMapTypeIdChanged: onMapTypeIdChangeHandler, scaleControl: true, scrollwheel: true, streetViewControl: false, 
            // Zoom came bundled in the camera control, so it needs enabling explicitly
            // wherever that control is switched off.
            zoomControl: isRefreshedDashboard ? true : undefined, zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM }, children: [_jsx(MapInstanceBinder, { setMap: setMap }), isOpsPlannerMap ? getOpsPlannerMapContent() : getLiveDashBoardMapContent()] })) }));
};
const LiveDashBoardOpsPlannerMap = (props) => {
    return (_jsx(APIProvider, { apiKey: API_KEY, children: _jsx(LiveDashBoardOpsPlannerMapContent, { ...props }) }));
};
export default LiveDashBoardOpsPlannerMap;
